



















































































































































































































import {
    ConfirmModal,
    ESearch,
    NavigationToggle,
    Pagination,
    Scrollbar,
    SortToggle,
    SvgImage,
    TwSelect,
} from '@/app/components';
import { TButton } from '@/app/components/common';
import { useFacetsFilters, useFeatureFlags, useSearch } from '@/app/composable';
import store from '@/app/store';
import { SortToggleOption } from '@/app/types';
import { useAlerts } from '@/modules/alert/composable';
import { AlertSourceType } from '@/modules/alert/constants/alert.constants';
import { RetrievalQueryAPI } from '@/modules/retrieval/api';
import { RetrievalQuery } from '@/modules/retrieval/interfaces';
import { SearchAPI } from '@/modules/search/api';
import { ContractsAPI } from '@/modules/sharing/api';
import { Icon } from '@iconify/vue2';
import { RefreshIcon } from '@vue-hero-icons/outline';
import { ExclamationIcon } from '@vue-hero-icons/solid';
import { computed, defineComponent, onBeforeUnmount, ref } from '@vue/composition-api';
import { BreedingRhombusSpinner } from 'epic-spinners';
import * as R from 'ramda';
import { AssetsAPI } from '../api';
import { AssetOverview } from '../components';
import { useAsset } from '../composable/asset';
import { useAssetFacets } from '../composable/asset-facets';
import { useAssetFilters } from '../composable/asset-filters';
import { AssetSortingOption, AssetTypeId, AssetTypes, StatusCode } from '../constants';

export default defineComponent({
    name: 'Assets',
    metaInfo: {
        title: 'My Assets',
    },
    components: {
        BreedingRhombusSpinner,
        AssetOverview,
        SvgImage,
        ConfirmModal,
        Pagination,
        TwSelect,
        Scrollbar,
        ESearch,
        RefreshIcon,
        TButton,
        ExclamationIcon,
        SortToggle,
        Icon,
        NavigationToggle,
    },
    setup(props, { root }) {
        const ownershipTabs = [
            { key: 'organisation', label: 'Own Organisation' },
            { key: 'acquired', label: 'Acquired' },
        ];
        const activeTab = ref<string>('organisation');
        const assetsDetails = computed(() => {
            const datasets = {
                own: [],
                acquired: [],
                sum: 0,
            };
            datasets.own = assets.value ? assets.value.filter((a: any) => a.assetType.id === AssetTypeId.Dataset) : [];
            datasets.acquired = assets.value.filter((a: any) => a.assetType.id === AssetTypeId.Dataset) ? [] : [];
            datasets.sum = datasets.own.length + datasets.acquired.length;
            return { datasets };
        });

        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');
        const isAnalyticsEnabled = flag('analytics');
        const isSharingEnabled = flag('sharing');
        const searchableFields = [
            {
                label: 'title',
                key: 'name',
                textColorClasses: 'text-primary-700',
                backgroundClasses: 'bg-primary-700',
                highlightClasses: 'bg-primary-300',
                widthClasses: 'pl-16 ml-2',
            },
            {
                label: 'description',
                key: 'description',
                textColorClasses: 'text-teal-700',
                backgroundClasses: 'bg-teal-700',
                highlightClasses: 'bg-teal-300',
                widthClasses: 'pl-28 ml-2',
            },
            {
                label: 'tags',
                key: 'metadata.general.tags',
                textColorClasses: 'text-purple-700',
                backgroundClasses: 'bg-purple-700',
                highlightClasses: 'bg-purple-300',
                widthClasses: 'pl-20 ',
            },
        ];
        const openFilters = ref<boolean>(true);
        const assetTabs = computed(() => {
            const tabs = [
                { key: 'all', label: 'All' },
                { key: 'datasets', label: 'Datasets' },
            ];
            if (isAnalyticsEnabled.value) tabs.push({ key: 'results', label: 'Results' });
            if (isAnalyticsEnabled.value) tabs.push({ key: 'models', label: 'Models' });
            return tabs;
        });
        const pageSize = 40;
        const showDeleteModal = ref(false);
        const toBeDeleted = ref<number | null>(null);
        const facets = ref<Record<string, { value: string; count: number; selected: boolean }[]>>({});
        const assets = ref<any>([]);
        const rejectedItems = ref<number>(0);
        const totalResults = ref<number>(0);
        const user = ref(store.state.auth.user);
        const affectedPipelinesAndQueries = ref<{ pipelines: number; queries: number }>({ pipelines: 0, queries: 0 });
        const acquiredAssetIds = ref<number[]>([]);
        const contracts = ref<
            {
                id: string;
                offlineRetention: string[];
                assetId: number | null;
                barteringAssetId: number | null;
            }[]
        >([]);

        const { checkAlert, fetchAlerts } = useAlerts(root, [AlertSourceType.Dataset, AlertSourceType.Result]);
        fetchAlerts();

        const refreshProvenanceIds = computed(() => store.state.notificationEngine.refresh.provenanceIds);

        const refresh = computed(
            () =>
                refreshProvenanceIds.value.length &&
                assets.value.find(
                    (asset: any) =>
                        asset.status === StatusCode.Uploading &&
                        asset.value?.provenanceAssetWorkflowIds &&
                        asset.value.provenanceAssetWorkflowIds.some((workflowId: string) =>
                            refreshProvenanceIds.value.includes(workflowId),
                        ),
                ),
        );

        const setSearchQuery = () => {
            switch (tab.value) {
                case AssetTypes.Datasets:
                    searchQuery.value.assetTypeId = AssetTypeId.Dataset;
                    searchQuery.value.facets = reducedDatasetFacets;
                    searchQuery.value.filters = reducedDatasetFilters;
                    break;
                case AssetTypes.Results:
                    searchQuery.value.assetTypeId = AssetTypeId.Result;
                    searchQuery.value.facets = reducedResultFacets;
                    searchQuery.value.filters = reducedResultFilters;
                    break;
                case AssetTypes.Models:
                    searchQuery.value.assetTypeId = AssetTypeId.Model;
                    searchQuery.value.facets = reducedModelFacets;
                    searchQuery.value.filters = reducedModelFilters;
                    break;
                default:
                    delete searchQuery.value.assetTypeId;
                    searchQuery.value.facets = reducedAssetFacets;
                    searchQuery.value.filters = reducedAssetFilters;
            }
        };

        const search = () => {
            setCommonSearchQueryValues();

            searchQuery.value.query.settings = query.value.settings;
            searchQuery.value.settings.excludeSchema = true;
            searchQuery.value.settings.userId = user.value.id;
            searchQuery.value.settings.own = activeTab.value === 'organisation';

            if (user.value.organisationId) searchQuery.value.settings.organisationId = user.value.organisationId;

            setQueryParams();

            const api =
                activeTab.value === 'organisation' ? SearchAPI.myAssetsFacetedSearch : SearchAPI.acquiredAssetsSearch;

            exec(api(searchQuery.value)).then((res: any) => {
                assets.value = res.data?.results;
                facets.value = res.data?.facets;
                rejectedItems.value = res.data?.accessControl?.rejectedItems || 0;
                totalResults.value = res.data?.total;

                // keep only selected statuses
                if (get('reset') && facets.value?.status) {
                    const facetStatus = facets.value.status
                        .filter((statusFacet: any) => statusFacet.selected)
                        .map((statusFacet: any) => statusFacet.value);
                    const statuses = R.clone(searchQuery.value.facets.status);
                    statuses.forEach((statusFacet: string) => {
                        if (!facetStatus.includes(statusFacet))
                            searchQuery.value.facets.status.splice(
                                searchQuery.value.facets.status.indexOf(statusFacet),
                                1,
                            );
                    });
                    root.$router.replace({
                        name: 'assets',
                        query: { status: searchQuery.value.facets.status.join(',') },
                    });
                }
            });
        };

        const {
            exec,
            loading,
            error,
            get,
            textNumberFilters,
            page,
            sortBy,
            tab,
            query,
            searchQuery,
            setTab,
            updateCurrentPage,
            facetChanged,
            filterChanged,
            sortByChanged,
            clearFilters,
            setCommonSearchQueryValues,
            setQueryParams,
            unsubscribeSearch,
            sortingFields,
            sortByKeyAndDirection,
        } = useSearch(root, 'assets', pageSize, AssetSortingOption, 'all', setSearchQuery, search, ['spatialCoverage']);

        onBeforeUnmount(() => {
            unsubscribeSearch.value ? unsubscribeSearch.value() : undefined;
            if (refresh.value) store.dispatch.notificationEngine.clearRefreshProvenanceIds();
        });

        const {
            assetFacets,
            datasetFacets,
            resultFacets,
            modelFacets,
            reducedAssetFacets,
            reducedDatasetFacets,
            reducedResultFacets,
            reducedModelFacets,
        } = useAssetFacets(get);

        const {
            assetFilters,
            datasetFilters,
            resultFilters,
            modelFilters,
            reducedAssetFilters,
            reducedDatasetFilters,
            reducedResultFilters,
            reducedModelFilters,
        } = useAssetFilters(get);

        const { calculateSearchedFacets, calculateSearchedFilters, defaultValue } = useFacetsFilters([
            'spatialCoverage',
        ]);

        const { assetTypeName } = useAsset();

        const assetTypeSelected = computed(() => {
            const assetType = Object.values(AssetTypes).includes(tab.value) ? tab.value : 'assets';
            if (rejectedItems.value === 1) return assetType.slice(0, -1);
            return assetType;
        });

        const facetsInfo = computed(() => {
            switch (tab.value) {
                case AssetTypes.Datasets:
                    return datasetFacets.value;
                case AssetTypes.Results:
                    return resultFacets.value;
                case AssetTypes.Models:
                    return modelFacets.value;
                default:
                    return assetFacets.value;
            }
        });

        const filtersInfo = computed(() => {
            switch (tab.value) {
                case AssetTypes.Datasets:
                    return datasetFilters.value;
                case AssetTypes.Results:
                    return resultFilters.value;
                case AssetTypes.Models:
                    return modelFilters.value;
                default:
                    return assetFilters.value;
            }
        });

        const currentSearchedFacetsFilters = computed(() =>
            calculateSearchedFacets(searchQuery.value.facets, facetsInfo.value).concat(
                calculateSearchedFilters(searchQuery.value.filters, filtersInfo.value),
            ),
        );

        const edit = (id: number, assetTypeId: number) => {
            root.$router.push({
                name: assetTypeId === 3 ? 'assets:model:edit' : 'assets:edit',
                params: { id: `${id}` },
            });
        };

        const view = (id: number) => {
            root.$router.push({ name: 'assets:view', params: { id: `${id}` } });
        };

        const navigate = (provenanceId: string, assetTypeId: number) => {
            root.$router.push({
                name: assetTypeId === AssetTypeId.Dataset ? 'data-checkin-jobs:edit' : 'workflow-designer:edit',
                params: { id: provenanceId, backTo: 'assets' },
            });
        };

        const destroy = async () => {
            if (toBeDeleted.value) {
                exec(AssetsAPI.deleteAsset(toBeDeleted.value))
                    .then(async (res: any) => {
                        const asset = assets.value.find((item: any) => item.id === toBeDeleted.value);
                        root.$set(asset, 'status', res.data.status);
                        root.$set(asset, 'updatedAt', res.data.updatedAt);
                        toBeDeleted.value = null;

                        (root as any).$toastr.s('Asset deleted successfuly', 'Success');
                        page.value = 1;
                        updateCurrentPage();
                    })
                    .catch((e) => {
                        if (e.response?.status === 403) {
                            (root as any).$toastr.e(
                                'You are not allowed to delete the specific asset as it is part of an active contract.',
                                'Deletion Forbidden!',
                            );
                        } else {
                            (root as any).$toastr.e('Deleting Asset failed', 'Error');
                        }
                    });
            }
        };

        const confirmationDeleteMessage = computed(() => {
            let message =
                'Deleting this asset will permanently remove all its data yet its metadata will remain for provenance reasons.';
            if (affectedPipelinesAndQueries.value.pipelines > 0 || affectedPipelinesAndQueries.value.queries > 0) {
                message = message + ' There ';
                message =
                    message +
                    `${
                        affectedPipelinesAndQueries.value.pipelines + affectedPipelinesAndQueries.value.queries > 1
                            ? 'are '
                            : 'is '
                    }`;
                if (affectedPipelinesAndQueries.value.pipelines > 0) {
                    message =
                        message +
                        `<span class= 'font-bold'>${affectedPipelinesAndQueries.value.pipelines}</span> Analytics Pipeline(s) `;
                }
                if (affectedPipelinesAndQueries.value.pipelines > 0 && affectedPipelinesAndQueries.value.queries > 0) {
                    message = message + 'and ';
                }
                if (affectedPipelinesAndQueries.value.queries > 0) {
                    message =
                        message +
                        `<span class= 'font-bold'>${affectedPipelinesAndQueries.value.queries}</span> Retrieval Querie(s) `;
                }
                message =
                    message +
                    `that ${
                        affectedPipelinesAndQueries.value.pipelines > 1 || affectedPipelinesAndQueries.value.queries > 1
                            ? 'are'
                            : 'is'
                    } currently using this asset and will be affected.`;
            }
            message =
                message +
                ' Please note that this action cannot be undone! Are you sure you still want to delete this asset?';
            return message;
        });

        const confirmDelete = (id: any) => {
            toBeDeleted.value = id;
            exec(AssetsAPI.getAffectedPipelinesQueries(id))
                .then((res: any) => {
                    affectedPipelinesAndQueries.value = res.data;
                    showDeleteModal.value = true;
                })
                .catch(() => {
                    (root as any).$toastr.e('Deleting Asset failed', 'Error');
                });
        };

        textNumberFilters.value = {
            relatedPipeline: defaultValue(get, 'relatedPipeline', false),
            assetOrigin: defaultValue(get, 'assetOrigin', false),
            derivativeAssets: defaultValue(get, 'derivativeAssets', false),
        };
        setSearchQuery();
        search();

        const archiveAsset = (id: number) => {
            exec(AssetsAPI.archiveAsset(id))
                .then(() => {
                    (root as any).$toastr.s('Model Asset archived successfuly', 'Success');
                    search();
                })
                .catch(() => {
                    (root as any).$toastr.e('Archiving Model Asset failed', 'Error');
                });
        };

        const restoreAsset = (id: number) => {
            exec(AssetsAPI.restoreArchivedAsset(id))
                .then(() => {
                    (root as any).$toastr.s('Model Asset restored successfuly', 'Success');
                    search();
                })
                .catch(() => {
                    (root as any).$toastr.e('Restoring Model Asset failed', 'Error');
                });
        };

        const modalConfirmed = () => {
            showDeleteModal.value = false;
            destroy();
        };

        const cancelAction = () => {
            showDeleteModal.value = false;
            toBeDeleted.value = null;
        };

        const refreshPage = () => {
            search();
            store.dispatch.notificationEngine.clearRefreshProvenanceIds();
        };

        const ownershipTabChanged = (ownershipTab: string) => {
            activeTab.value = ownershipTab;
            search();
        };

        const sortToggle = computed({
            get: (): { key: string; direction: string } => {
                return {
                    key: searchQuery.value.sortBy.field,
                    direction: searchQuery.value.sortBy.asc ? 'asc' : 'desc',
                };
            },
            set: (updatedSort: { key: string; direction: string }) => {
                sortByKeyAndDirection(updatedSort);
            },
        });
        const sortToggleOptions: SortToggleOption[] = [
            {
                key: 'date_updated',
                directions: [
                    { label: 'Last updated', direction: 'desc' },
                    { label: 'Oldest updated', direction: 'asc' },
                ],
            },
            {
                key: 'title',
                directions: [
                    { label: 'Title A-Z', direction: 'asc' },
                    { label: 'Title Z-A', direction: 'desc' },
                ],
            },
        ];

        const calculateFacetOptions = (facet: string) => {
            if (R.has(facet, facets.value))
                return facets.value[facet].map((val: { value: string; count: number; selected: boolean }) => {
                    return {
                        value: val.value,
                        label: facet === 'assetType' ? assetTypeName(parseInt(val.value, 10)) : val.value,
                    };
                });
            return [];
        };

        const assetContract = (assetId: number) => {
            const contract = contracts.value.find((c) => c.assetId === assetId || c.barteringAssetId === assetId);
            return contract ? R.pick(['id', 'offlineRetention'], contract) : undefined;
        };

        if (isSharingEnabled.value) {
            exec(ContractsAPI.getActive()).then((res: any) => {
                contracts.value = res?.data;
            });

            exec(RetrievalQueryAPI.getAcquiredAssetQueries()).then((res) => {
                if (res?.data)
                    acquiredAssetIds.value = R.uniq(
                        res.data.reduce((acc: number[], rq: RetrievalQuery) => R.concat(acc, rq.assetIds ?? []), []),
                    );
            });
        }

        return {
            loading,
            error,
            assets,
            edit,
            view,
            destroy,
            confirmDelete,
            showDeleteModal,
            tab,
            assetTabs,
            setTab,
            archiveAsset,
            restoreAsset,
            modalConfirmed,
            cancelAction,
            pageSize,
            totalResults,
            updateCurrentPage,
            openFilters,
            navigate,
            facets,
            facetChanged,
            searchQuery,
            sortingFields,
            sortByChanged,
            query,
            sortBy,
            page,
            rejectedItems,
            assetTypeSelected,
            filterChanged,
            clearFilters,
            facetsInfo,
            filtersInfo,
            currentSearchedFacetsFilters,
            textNumberFilters,
            refresh,
            refreshPage,
            affectedPipelinesAndQueries,
            confirmationDeleteMessage,
            checkAlert,
            searchableFields,
            isAlertsEnabled,
            isAnalyticsEnabled,
            sortToggle,
            sortToggleOptions,
            calculateFacetOptions,
            assetContract,
            acquiredAssetIds,
            ownershipTabs,
            activeTab,
            assetsDetails,
            ownershipTabChanged,
        };
    },
});
